import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Breadcrumb } from '../../app/Breadcrumb'
import taskDetailJson from '../../demo/fixtures/task-detail.json'
import { demoProducts } from '../../demo/store'
import { selectAutopilot, selectOptimizations, useCampaign } from '../../demo/selectors'
import { useDemoStore } from '../../demo/store'
import type { CampaignSettingsPatch } from '../../demo/types'
import { EmptyState, Toast, ToastHost } from '../../ui'
import { AudienceAnalysis } from './AudienceAnalysis'
import { AutopilotSection } from './AutopilotSection'
import { ComplianceAlerts } from './ComplianceAlerts'
import { CreativeSection } from './CreativeSection'
import { CreativeStrategy } from './CreativeStrategy'
import { DetailHeader } from './DetailHeader'
import { EditSettingsDialog } from './EditSettingsDialog'
import { LinkAlert } from './LinkAlert'
import { LinkFixDialog } from './LinkFixDialog'
import { ProductAnalysis } from './ProductAnalysis'
import styles from './TaskDetailPage.module.css'

interface TaskFixture {
  regenerateMs: { productAnalysis: number; creativeStrategies: number }
  confirmToast: string
}

const taskFixture = taskDetailJson as unknown as TaskFixture

function linkHost(value: string): string {
  try { return new URL(value).host } catch { return value }
}

export function TaskDetailPage() {
  const { campaignId = '' } = useParams()
  const campaign = useCampaign(campaignId)
  const product = demoProducts.find((item) => item.id === campaign?.productId)
  const variant = useDemoStore((state) => state.variantByCampaign[campaignId]) ?? { productAnalysis: 0 as const, creativeStrategies: 0 as const }
  const confirmCampaign = useDemoStore((state) => state.confirmCampaign)
  const pauseCampaign = useDemoStore((state) => state.pauseCampaign)
  const resumeCampaign = useDemoStore((state) => state.resumeCampaign)
  const fixProductLink = useDemoStore((state) => state.fixProductLink)
  const updateSettings = useDemoStore((state) => state.updateSettings)
  const cycleVariant = useDemoStore((state) => state.cycleVariant)
  const autopilotByCampaign = useDemoStore((state) => state.autopilotByCampaign)
  const optimizationState = useDemoStore((state) => state.optimizations)
  const [primaryBusy, setPrimaryBusy] = useState(false)
  const [productLoading, setProductLoading] = useState(false)
  const [strategyLoading, setStrategyLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [focusAudience, setFocusAudience] = useState(false)
  const [fixOpen, setFixOpen] = useState(false)
  const [audienceEdited, setAudienceEdited] = useState(false)
  const productRef = useRef<HTMLDivElement>(null)
  const audienceRef = useRef<HTMLDivElement>(null)
  const strategyRef = useRef<HTMLDivElement>(null)
  const creativeRef = useRef<HTMLDivElement>(null)
  const autopilot = useMemo(() => { void autopilotByCampaign; return selectAutopilot(campaignId) }, [autopilotByCampaign, campaignId])
  const optimizations = useMemo(() => { void optimizationState; return selectOptimizations(campaignId) }, [campaignId, optimizationState])

  if (!campaign || !product) {
    return <><div data-scope="detail-breadcrumb"><Breadcrumb parent="广告监控" parentTo="/monitor" current="广告不存在" /></div><EmptyState icon="inbox" title="没有找到这条广告" text="它可能已被重置或移除" /><Link className={styles.missingLink} to="/monitor">返回广告监控</Link></>
  }

  const primaryAction = async () => {
    if (primaryBusy || campaign.status === 'attention' || campaign.status === 'completed') return
    setPrimaryBusy(true)
    await new Promise((resolve) => window.setTimeout(resolve, 600))
    if (campaign.status === 'pending') {
      confirmCampaign(campaign.id)
      setToast(taskFixture.confirmToast)
    } else if (campaign.status === 'running') {
      pauseCampaign(campaign.id)
      setToast('已暂停投放')
    } else if (campaign.status === 'paused') {
      resumeCampaign(campaign.id)
      setToast('已恢复投放')
    }
    setPrimaryBusy(false)
  }

  const regenerate = async (key: 'productAnalysis' | 'creativeStrategies') => {
    const setLoading = key === 'productAnalysis' ? setProductLoading : setStrategyLoading
    setLoading(true)
    await new Promise((resolve) => window.setTimeout(resolve, taskFixture.regenerateMs[key]))
    cycleVariant(campaign.id, key)
    setLoading(false)
  }

  const openEditor = (audienceOnly: boolean) => {
    setFocusAudience(audienceOnly)
    setEditOpen(true)
  }
  const saveSettings = (patch: CampaignSettingsPatch) => {
    if (optimizations.some((item) => item.type === 'audience' && !item.undone)) setAudienceEdited(true)
    updateSettings(campaign.id, patch)
    setEditOpen(false)
  }

  return (
    <div className={styles.page}>
      <div data-scope="detail-breadcrumb"><Breadcrumb parent="广告监控" parentTo="/monitor" current={campaign.adName} /></div>
      <DetailHeader campaign={campaign} product={product} busy={primaryBusy} autopilotOn={autopilot?.on === true} onEdit={() => openEditor(false)} onPrimary={() => void primaryAction()} />
      {campaign.status === 'attention' && campaign.attentionReason === 'product_link_invalid' ? <LinkAlert onFix={() => setFixOpen(true)} /> : null}
      <ComplianceAlerts campaignId={campaign.id} />
      <AutopilotSection key={campaign.id} campaign={campaign} audienceEdited={audienceEdited} targets={{ product: productRef, audience: audienceRef, strategy: strategyRef, creative: creativeRef, compliance: creativeRef }} onOpenSettings={() => openEditor(false)} onOpenLink={() => setFixOpen(true)} onToast={setToast} />
      <div className={styles.analysisGrid}>
        <div ref={productRef}><ProductAnalysis campaignId={campaign.id} variant={variant.productAnalysis} loading={productLoading} onRegenerate={() => void regenerate('productAnalysis')} /></div>
        <div ref={audienceRef}><AudienceAnalysis campaignId={campaign.id} onAdjust={() => openEditor(true)} /></div>
      </div>
      <div ref={strategyRef} className={styles.sectionWrap}><CreativeStrategy campaignId={campaign.id} variant={variant.creativeStrategies} loading={strategyLoading} onRegenerate={() => void regenerate('creativeStrategies')} /></div>
      <div ref={creativeRef} className={styles.sectionWrap}><CreativeSection campaignId={campaign.id} /></div>
      {editOpen ? <EditSettingsDialog open focusAudience={focusAudience} campaign={campaign} onClose={() => setEditOpen(false)} onSave={saveSettings} /> : null}
      {fixOpen ? <LinkFixDialog open initialUrl={product.url} onClose={() => setFixOpen(false)} onFixed={(url) => { fixProductLink(campaign.id, url); setToast(`商品链接已更新为 ${linkHost(url)}，广告恢复投放`) }} /> : null}
      {toast ? <ToastHost><div data-scope="state-toast"><Toast onDismiss={() => setToast(null)}>{toast}</Toast></div></ToastHost> : null}
    </div>
  )
}
